import React from "react";
import { TooltipHost, DirectionalHint } from "@fluentui/react";
import { useTheme } from "../../theme/OAS365Theme";

const Tooltip = ({
  children,
  content,
  position = "top",
  delay = 300,
  gapSpace = 8,
  maxWidth = 300,
  disabled = false,
  className = "",
  style = {},
  listType = "ul",
  boldLabels = false,
  fieldFilter = null
}) => {

  const theme = useTheme();

  if (!content || disabled) return children;

  const getDirectionalHint = () => {
    switch (position) {
      case "bottom":
        return DirectionalHint.bottomCenter;
      case "bottomLeft":
        return DirectionalHint.bottomLeftEdge;
      case "bottomRight":
        return DirectionalHint.bottomRightEdge;
      case "left":
        return DirectionalHint.leftCenter;
      case "right":
        return DirectionalHint.rightCenter;
      default:
        return DirectionalHint.topCenter;
    }
  };

  const renderContent = () => {

    // OBJECT CONTENT
    if (typeof content === "object" && !Array.isArray(content) && !React.isValidElement(content)) {

      const entries = Object.entries(content);
      const filteredEntries = fieldFilter
        ? entries.filter(([k, v]) => fieldFilter(k, v))
        : entries;

      const ListTag = listType === "ol" ? "ol" : listType === "div" ? "div" : "ul";

      return (
        <ListTag
          style={{
            margin: 0,
            paddingLeft: listType === "div" ? 0 : "16px",
            color: theme.colors.addovationWhite
          }}
        >
          {filteredEntries.map(([key, value], index) => (
            <li
              key={index}
              style={{
                marginBottom: 2,
                fontSize: "inherit",
                lineHeight: "1.3",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere"
              }}
            >
              {boldLabels ? (
                <>
                  <strong>{key}:</strong> {value || "N/A"}
                </>
              ) : (
                `${key}: ${value || "N/A"}`
              )}
            </li>
          ))}
        </ListTag>
      );
    }

    // ARRAY CONTENT
    if (Array.isArray(content)) {

      const ListTag = listType === "ol" ? "ol" : listType === "div" ? "div" : "ul";
      const ItemTag = listType === "div" ? "div" : "li";

      return (
        <ListTag
          style={{
            margin: 0,
            paddingLeft: listType === "div" ? 0 : "16px",
            color: theme.colors.addovationWhite
          }}
        >
          {content.filter(Boolean).map((item, index) => (
            <ItemTag
              key={index}
              style={{
                marginBottom: 2,
                color: theme.colors.addovationWhite,
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere"
              }}
            >
              {item}
            </ItemTag>
          ))}
        </ListTag>
      );
    }

    // STRING / REACT ELEMENT
    return (
      <div
        style={{
          width: "fit-content",
          maxWidth: "100%",
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          color: theme.colors.addovationWhite,
        }}
      >
        {content}
      </div>
    );
  };

  return (
    <TooltipHost
      content={renderContent()}
      delay={delay}
      directionalHint={getDirectionalHint()}
      styles={{
        root: { display: "inline-block" },

        content: {
          backgroundColor: "transparent",
          color: theme.colors.addovationWhite,
          padding: "8px 12px",
          fontSize: theme.fontSizes.menusize,
          fontFamily: theme.fonts.regular,
          fontWeight: theme.fontWeight.normal,
          lineHeight: theme.lineHeight.small,

          width: "fit-content",
          maxWidth: `${maxWidth}px`,

          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",

          border: "none"
        }
      }}
      calloutProps={{
        gapSpace,
        isBeakVisible: true,
        beakWidth: 12,
        styles: {
          root: {
            borderRadius: theme.border.radius.textField
          },

          calloutMain: {
            backgroundColor: theme.colors.themeDarkColor,
            color: theme.colors.addovationWhite,

            borderRadius: theme.border.radius.textField,
            padding: 0,

            width: "fit-content",
            maxWidth: `${maxWidth}px`,

            border: "none",
            overflow: "hidden"
          },

          beak: {
            backgroundColor: theme.colors.themeDarkColor
          },

          beakCurtain: {
            backgroundColor: theme.colors.themeDarkColor,
            borderRadius: theme.border.radius.textField
          }
        }
      }}
      className={className}
    >
      <div style={{ display: "inline-block", ...style }}>
        {children}
      </div>
    </TooltipHost>
  );
};

export default Tooltip;